// Movement History — the back face of the movement modules (flip via the
// header clock icon). Shows a 7-day pre-match price walk for each of a team's
// last 10 matches, one line per match, so the user can compare how this
// market typically moves before kick-off. The selected opponent's line is
// highlighted.
//
// Pure: types + the view transforms. Every view is a transform of the SAME
// offset-adjusted odds series — there is one source of truth per match.

import type { MovementPoint } from "./movement.js";

/** One past match: the opponent + its pre-match price series (168h → NOW). */
export interface HistoryMatch {
  opponent: string;
  prices: MovementPoint[];
}

/** Team (or player) → its last 10 matches (most recent first). Winner keys by
 *  team; Player Goals/Cards key by player name. */
export type MovementHistoryData = Record<string, HistoryMatch[]>;

/** Match Goals/Cards: team → "Match" (match-total lines) | "Team" (that team's
 *  own lines) → line key (e.g. "over_2.5") → its last 10 matches. Which one the
 *  back face reads follows the front face's scope. */
export type LineHistoryData = Record<
  string,
  Record<"Match" | "Team", Record<string, HistoryMatch[]>>
>;

export type HistoryViewKey = "odds" | "ah" | "rebased" | "pct";

export interface HistoryView {
  label: string;
  /** Transform an odds series into this view's values; null = plain odds. */
  fn: ((pts: MovementPoint[]) => MovementPoint[]) | null;
  /** Format a value for the y-axis. */
  fmt: (v: number) => string;
}

const signed = (v: number, dp: number) => (v > 0 ? "+" : "") + v.toFixed(dp);

/** Rough Asian Handicap line from the win price's implied probability, to the
 *  nearest quarter line. Only meaningful for a match-result (1x2) market. */
function toAsianHandicap(pts: MovementPoint[]): MovementPoint[] {
  return pts.map((d) => ({ t: d.t, o: Math.round((1 - 2 / d.o) * 3 * 4) / 4 }));
}

/** Change in odds points from the first (168h) price. */
function toRebased(pts: MovementPoint[]): MovementPoint[] {
  const start = pts[0]?.o ?? 0;
  return pts.map((d) => ({ t: d.t, o: Math.round((d.o - start) * 100) / 100 }));
}

/** % change from the first (168h) price. */
function toPercentChange(pts: MovementPoint[]): MovementPoint[] {
  const start = pts[0]?.o || 1;
  return pts.map((d) => ({ t: d.t, o: Math.round(((d.o - start) / start) * 1000) / 10 }));
}

export const HISTORY_VIEWS: Record<HistoryViewKey, HistoryView> = {
  odds: { label: "Odds", fn: null, fmt: (v) => v.toFixed(2) },
  ah: { label: "Asian Handicap", fn: toAsianHandicap, fmt: (v) => signed(v, 2) },
  rebased: { label: "Change (pts)", fn: toRebased, fmt: (v) => signed(v, 2) },
  pct: { label: "% Change", fn: toPercentChange, fmt: (v) => signed(v, 1) + "%" },
};

export interface HistoryLine {
  opponent: string;
  pts: MovementPoint[];
}

/** Apply the bookmaker offset (2dp), then the view transform, to every match. */
export function shapeHistoryLines(
  matches: readonly HistoryMatch[],
  offset: number,
  view: HistoryViewKey
): HistoryLine[] {
  const fn = HISTORY_VIEWS[view]?.fn ?? null;
  return matches.map((m) => {
    const pts = m.prices.map((d) => ({ t: d.t, o: Math.round((d.o + offset) * 100) / 100 }));
    return { opponent: m.opponent, pts: fn ? fn(pts) : pts };
  });
}
